import React from 'react';
import Card from '../components/common/Card';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto py-12 px-4 space-y-8">
        {/* Header */}
        <section className="text-center">
          <h1 className="text-xl sm:text-2xl font-bold text-emphasis mb-4">Privacy Policy</h1>
          <p className="text-secondary">Last updated: March 2024</p>
        </section>

        {/* Content */}
        <Card className="p-8">
          <div className="prose prose-sm max-w-none">
            <section className="mb-6">
              <h2 className="text-xl font-bold text-emphasis mb-3">1. Introduction</h2>
              <p className="text-secondary mb-3">
                At SALON, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform. Please read this privacy policy carefully. If you do not agree with the terms of this privacy policy, please do not access the platform.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-bold text-emphasis mb-3">2. Information We Collect</h2>
              <p className="text-secondary mb-3">We may collect personal information that you voluntarily provide to us when you:</p>
              <ul className="list-disc list-inside text-secondary space-y-2 ml-4">
                <li>Register on the platform</li>
                <li>Express an interest in obtaining information about us or our products and services</li>
                <li>Participate in activities on the platform</li>
                <li>Contact us</li>
              </ul>
              <p className="text-secondary mt-3 mb-3">The personal information that we collect depends on the context of your interactions with us and the platform, the choices you make, and the products and features you use. The personal information we collect may include the following:</p>
              <ul className="list-disc list-inside text-secondary space-y-2 ml-4">
                <li>Personal Identity Information (name, email, phone number)</li>
                <li>Booking history and preferences</li>
                <li>Payment information (processed securely through our payment partners)</li>
                <li>Location data (with your consent)</li>
                <li>Communication preferences</li>
              </ul>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-bold text-emphasis mb-3">3. How We Use Your Information</h2>
              <p className="text-secondary mb-3">We use personal information collected via our platform for a variety of business purposes described below. We process your personal information for these purposes in reliance on our legitimate business interests, in order to enter into or perform a contract with you, with your consent, and/or for compliance with our legal obligations.</p>
              <ul className="list-disc list-inside text-secondary space-y-2 ml-4">
                <li>To facilitate account creation and logon process</li>
                <li>To send administrative information to you</li>
                <li>To fulfill and manage your bookings</li>
                <li>To post testimonials (with your consent)</li>
                <li>To request feedback</li>
                <li>To protect our Services</li>
                <li>To enforce our terms, conditions and policies</li>
                <li>To respond to legal requests and prevent harm</li>
              </ul>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-bold text-emphasis mb-3">4. Sharing Your Information</h2>
              <p className="text-secondary mb-3">We only share information with the following third parties:</p>
              <ul className="list-disc list-inside text-secondary space-y-2 ml-4">
                <li>Service Providers - to monitor and analyze the use of our Service</li>
                <li>Payment Processors - to process payments for services</li>
                <li>Business Transfers - in connection with a merger, sale of company assets, or acquisition</li>
                <li>Legal Obligations - when required by law or in response to valid requests</li>
              </ul>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-bold text-emphasis mb-3">5. Data Security</h2>
              <p className="text-secondary mb-3">
                We have implemented appropriate technical and organizational security measures designed to protect the security of any personal information we process. However, despite our safeguards and efforts to secure your information, no electronic transmission over the Internet or information storage technology can be guaranteed to be 100% secure, so we cannot promise or guarantee that hackers, cybercriminals, or other unauthorized third parties will not be able to defeat our security, and improperly collect, access, steal, or modify your information.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-bold text-emphasis mb-3">6. Your Rights</h2>
              <p className="text-secondary mb-3">Depending on your location, you may have the following rights:</p>
              <ul className="list-disc list-inside text-secondary space-y-2 ml-4">
                <li>Right to access - You have the right to request copies of your personal information</li>
                <li>Right to rectification - You have the right to request that we correct any information you believe is inaccurate</li>
                <li>Right to erasure - You have the right to request that we erase your personal information</li>
                <li>Right to restrict processing - You have the right to request that we restrict the processing of your information</li>
                <li>Right to object to processing - You have the right to object to our processing of your personal information</li>
                <li>Right to data portability - You have the right to request that we transfer the information we collected to another organization</li>
              </ul>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-bold text-emphasis mb-3">7. Cookies and Tracking Technologies</h2>
              <p className="text-secondary mb-3">
                We use Cookies and similar tracking technologies (like web beacons and pixels) to access or store information. Specific information about how we use such technologies and how you can refuse certain Cookies is set out in our Cookie Policy.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-bold text-emphasis mb-3">8. Children's Privacy</h2>
              <p className="text-secondary mb-3">
                Our Service does not address anyone under the age of 13. We do not knowingly collect personally identifiable information from anyone under the age of 13. If you are a parent or guardian and you are aware that your child has provided us with Personal Data, please contact us.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-bold text-emphasis mb-3">9. Changes to This Privacy Policy</h2>
              <p className="text-secondary mb-3">
                We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date at the top of this Privacy Policy. You are advised to review this Privacy Policy periodically for any changes.
              </p>
            </section>

            <section className="mb-6">
              <h2 className="text-xl font-bold text-emphasis mb-3">10. Contact Us</h2>
              <p className="text-secondary mb-3">
                If you have any questions about this Privacy Policy, please contact us:
              </p>
              <ul className="list-disc list-inside text-secondary space-y-2 ml-4">
                <li>By email: privacy@salon.com</li>
                <li>By visiting our contact page: /contact</li>
              </ul>
            </section>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
